import type { ActivityCounts, ActivityRange, TeamActivityMember } from '@/api/gamma-team/types';

import { toActivityDateRange, toIsoDay } from './period';
import { latestActivityAt, memberName } from './team-sorting';

interface TeamCountColumn {
  key: keyof ActivityCounts;
  header: string;
  shortHeader?: string;
  description?: string;
}

export const TEAM_COUNT_COLUMNS: TeamCountColumn[] = [
  {
    key: 'contactsReached',
    header: 'Contacts reached',
    shortHeader: 'Contacts',
    description: 'Distinct contacts with at least one entry in the period',
  },
  {
    key: 'entries',
    header: 'Entries',
    description: 'Calls, messages, meetings and notes logged in Contact History',
  },
  { key: 'calls', header: 'Calls' },
  { key: 'messages', header: 'Messages' },
  { key: 'meetings', header: 'Meetings' },
  { key: 'notes', header: 'Notes' },
  { key: 'dealsCreated', header: 'Deals created' },
  { key: 'dealsAdvanced', header: 'Deals advanced' },
];

const EXCEL_UTF8_BOM = String.fromCharCode(0xfeff);

const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

export const csvCell = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return String(value);
  const guarded = FORMULA_TRIGGER.test(value) ? `'${value}` : value;
  return /[",\n\r]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
};

export const buildTeamActivityCsv = (
  members: readonly TeamActivityMember[],
  total: ActivityCounts | null,
): string => {
  const header = [
    'Team member',
    'Email',
    'Role',
    'Former',
    ...TEAM_COUNT_COLUMNS.map(({ header: label }) => label),
    'Last active',
  ];
  const rows = members.map((member) => [
    memberName(member),
    member.email ?? '',
    member.role ?? '',
    member.former ? 'Yes' : 'No',
    ...TEAM_COUNT_COLUMNS.map(({ key }) => member[key]),
    latestActivityAt(member) ?? '',
  ]);
  const totalRow = total
    ? [['Total', '', '', '', ...TEAM_COUNT_COLUMNS.map(({ key }) => total[key]), '']]
    : [];

  return [header, ...rows, ...totalRow]
    .map((row) => row.map((cell) => csvCell(cell)).join(','))
    .join('\r\n');
};

export const teamActivityCsvFileName = (range: ActivityRange | undefined): string => {
  const days = range ? toActivityDateRange(range) : null;
  return days
    ? `team-activity_${toIsoDay(days.from)}_${toIsoDay(days.to)}.csv`
    : 'team-activity.csv';
};

export const downloadCsv = (csv: string, fileName: string): void => {
  const blob = new Blob([EXCEL_UTF8_BOM, csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};
